import { useRef, useState, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { ActionIcon, Group, Loader, Stack, Text } from '@mantine/core';
import { IconPlayerPlay, IconPlayerPause } from '@tabler/icons-react';

// Utility function to format seconds into MM:SS
const formatTime = (seconds) => {
    if (isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const AudioVisualizer = ({ audioUrl }) => {
    const containerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [wavesurfer, setWavesurfer] = useState(null);
    const [currentTime, setCurrentTime] = useState('00:00');
    const [duration, setDuration] = useState('00:00'); // New state for max time

    useEffect(() => {
        if (!containerRef.current) return;

        setIsLoading(true);

        // Initialize wavesurfer
        const ws = WaveSurfer.create({
            container: containerRef.current,
            waveColor: '#666',
            progressColor: '#0d0',
            url: audioUrl,
            height: 128,
            barWidth: 4,
            barGap: 2,
            barRadius: 4,
            cursorWidth: 0,
        });

        // Event listeners
        ws.on('ready', () => {
            setIsLoading(false);
            // Get the total duration once the audio is ready
            setDuration(formatTime(ws.getDuration()));
        });

        // Updates the timer during playback
        ws.on('timeupdate', (time) => {
            setCurrentTime(formatTime(time));
        });

        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => setIsPlaying(false));

        setWavesurfer(ws);

        // Cleanup on unmount
        return () => {
            ws.destroy();
        };
    }, [audioUrl]);

    const handlePlayPause = (e) => {
        e.preventDefault();
        if (wavesurfer && !isLoading) {
            wavesurfer.playPause();
        }
    };

    return (
        <Stack style={{ width: '100%' }} gap="md">
            {/* Top row: Current Time -> Waveform/Loader -> Max Time */}
            <Group style={{ width: '100%' }} wrap="nowrap" align="center">

                {/* Current Time on the left */}
                <Text fw={500} size="md" c="dimmed" style={{ minWidth: '45px', textAlign: 'center' }}>
                    {currentTime}
                </Text>

                {/* Main container for the waveform and loader */}
                <div style={{ flexGrow: 1, minWidth: '200px', position: 'relative', height: '128px' }}>
                    {/* Loader */}
                    {isLoading && (
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10 // Added z-index to ensure it sits on top of the hidden canvas
                        }}>
                            <Loader
                                size="xl"
                                type="dots"
                                color="primary" // Switched to Mantine's theme color string to ensure visibility
                            />
                        </div>
                    )}

                    {/* Waveform container */}
                    <div
                        ref={containerRef}
                        style={{
                            width: '100%',
                            opacity: isLoading ? 0 : 1,
                            transition: 'opacity 0.2s ease'
                        }}
                    />
                </div>

                {/* Max Time on the right */}
                <Text fw={500} size="md" c="dimmed" style={{ minWidth: '45px', textAlign: 'center' }}>
                    {duration}
                </Text>
            </Group>

            {/* Bottom row: Centered Play/Pause Button */}
            <Group justify="center">
                <ActionIcon
                    onClick={handlePlayPause}
                    variant="light"
                    color="primary"
                    radius="xl"
                    size="xl"
                    disabled={isLoading}
                >
                    {isPlaying ? <IconPlayerPause /> : <IconPlayerPlay />}
                </ActionIcon>
            </Group>
        </Stack>
    );
};

export default AudioVisualizer;